import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Shield, Mail, AlertTriangle } from "lucide-react";
import { useAuthStore } from "../stores/auth-store";
import { useThemeStore } from "../stores/themeStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { isCryptoAvailable } from "../lib/crypto";

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(8, "Senha deve ter pelo menos 8 caracteres"),
});

export default function LoginPage() {
  const { login, isLoading } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });
  
  const onSubmit = async (data) => {
    if (!isCryptoAvailable()) {
      toast.error("Seu navegador não suporta criptografia. Use um navegador moderno.");
      return;
    }
    
    const result = await login(data.email, data.password);
    
    if (result.success) {
      toast.success("Login realizado com sucesso!");
      navigate(result.unlocked ? "/dashboard" : "/unlock");
    } else {
      toast.error(result.error);
    }
  };
  
  return (
    <div className="h-screen h-dvh bg-background flex overflow-hidden">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-4">
        <div className="mx-auto w-full max-w-sm">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="w-9 h-9 text-secondary" />
            <span className="text-3xl font-bold tracking-tight">KawaFinance</span>
          </div>
          
          <h1 className="text-2xl font-semibold text-center mb-2">
            Bem-vindo de volta
          </h1>
          <p className="text-muted-foreground text-center mb-6">
            Entre com suas credenciais para acessar seu cofre
          </p>
          
          {/* Security Warning */}
          <div className="alert-banner alert-warning mb-5">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Criptografia End-to-End</p>
              <p className="text-xs mt-1 opacity-80">
                O login autentica seu usuário. A chave dos dados será solicitada no desbloqueio do cofre.
              </p>
            </div>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  className="pl-10"
                  {...register("email")}
                  data-testid="login-email"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="password">Senha</Label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Esqueci minha senha
                </Link>
              </div>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                showLockIcon
                {...register("password")}
                data-testid="login-password"
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            
            <Button
              type="submit"
              className="w-full btn-glow"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
          
          <p className="text-center text-muted-foreground mt-5">
            Não tem uma conta?{" "}
            <Link to="/register" className="text-primary hover:underline font-medium">
              Criar conta
            </Link>
          </p>
          
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="mx-auto mt-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {theme === "dark" ? "Modo Claro" : "Modo Escuro"}
          </button>
        </div>
      </div>
      
      {/* Right Side - Image (Desktop only) */}
      <div className="hidden lg:block lg:w-1/2 h-full relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-primary/20" />
        <img
          src="https://images.unsplash.com/photo-1703290858674-4895a3a2e25f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBnbGFzcyUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMDNkJTIwcmVuZGVyfGVufDB8fHx8MTc3MTcxMDUxMHww&ixlib=rb-4.1.0&q=85"
          alt="Abstract 3D shapes"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <Shield className="w-16 h-16 mx-auto mb-4 drop-shadow-lg" />
            <h2 className="text-3xl font-bold mb-2 drop-shadow-lg">Seus dados, sua chave</h2>
            <p className="text-lg opacity-90 drop-shadow">
              Criptografia End-to-End real. Nem nós podemos ver seus dados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
